import { deliveryLast } from '../invoice-delivery.js';
/**
 * Модель печатных документов «Счёт» и «Акт» — §2.5, §2.6.
 *
 * Структура и КАЖДАЯ формулировка сверены с 58 реальными документами
 * лаборатории (29 счетов и 29 актов) — вплоть до объединений ячеек и того,
 * что в акте итог является последней строкой ТОЙ ЖЕ таблицы позиций.
 * Бухгалтерии клиник сверяют присланное с привычным, и расхождение
 * в структуре порождает вопросы.
 *
 * Единственное осознанное отступление от образцов — ФОРМАТ СУММЫ. В бумагах
 * лаборатории счёт печатал «14000.00», а акт «14 000,00»; здесь оба печатают
 * «14000.00». Образцы набирали в разное время и в разных программах, а счёт
 * с актом уходят вместе, одной сделкой.
 *
 * Счёт и акт — РАЗНЫЕ документы, а не два вида одного:
 *
 *   счёт  предъявляется к оплате. Подписывает его только исполнитель, поэтому
 *         подписанта заказчика в нём нет. Сверху — банковская платёжка.
 *   акт   подтверждает выполнение. Подписывают обе стороны, поэтому здесь
 *         есть и подписант заказчика, и его банковские реквизиты, и «МП».
 *
 * Здесь считается ЧТО печатать. Отдельные модули решают, КАК: `html.ts` для
 * превью и PDF, `docx.ts` на сервере для Word. Все форматы обязаны показывать
 * одинаковые числа, поэтому арифметика существует в одном месте.
 */
import { dayOf, formatDateDots, formatDateLong, monthYearGenitive, } from '../dates.js';
import { withForm } from '../legal-name.js';
import { formatKopDoc, lineTotalKop } from '../money.js';
import { kopToWordsRu, kopToWordsRuDigits } from '../number-to-words-ru.js';
import { invoiceNumberText } from '../invoice-number.js';
import { signerInPersonOf, signerSignatureOf } from '../signer.js';
/**
 * Оговорка об отсутствии претензий.
 *
 * Перенесена из документов лаборатории ДОСЛОВНО, включая «объему» без «ё»:
 * это юридическая формулировка, а не текст, который стоит улучшать. Повтор
 * слова «претензий» из акта не воспроизводится — он там опечатка набора.
 */
const DISCLAIMER = 'Вышеперечисленные услуги выполнены полностью и в срок. Заказчик претензий по объему, качеству и срокам оказания услуг не имеет.';
/** Итог документа: сумма позиций с округлением каждой строки до копейки. */
export function documentTotalKop(items) {
    return items.reduce((sum, item) => sum + lineTotalKop(item.priceKop, item.qty), 0);
}
/**
 * Имя исполнителя в трёх формах — по одной на каждое место в документах.
 *
 * В файлах лаборатории оно записано ПО-РАЗНОМУ, и это не небрежность:
 *
 *   платёжка счёта   «Ханеев Дмитрий Олегович (ИП)»   — как в банковской карточке
 *   «Поставщик» счёта «ИП Ханеев Дмитрий Олегович»    — коротко, строка и так длинная
 *   «Исполнитель» акта «Индивидуальный предприниматель Ханеев Дмитрий Олегович»
 *
 * Все три выводятся из полного имени, а не хранятся отдельными полями: иначе
 * их пришлось бы заполнять втроём и следить, чтобы они не разошлись.
 */
const IP_PREFIX = 'Индивидуальный предприниматель ';
/*
 * Имя исполнителя берётся из НАБОРА НАСТРОЕК со свободными полями
 * (Record<string, string>), а не из строгой записи: ключ там может и
 * отсутствовать — у старой настройки, после переименования поля, в проверочных
 * данных. Пустая строка вместо падения: документ покажет пустое место, а не
 * обрушит весь раздел.
 */
const имя = (name) => name ?? '';
/** «Индивидуальный предприниматель Ханеев Д.О.» → «Ханеев Д.О.» */
function withoutForm(name) {
    return имя(name).startsWith(IP_PREFIX) ? имя(name).slice(IP_PREFIX.length) : имя(name);
}
/** Для платёжки: «Ханеев Дмитрий Олегович (ИП)». */
export function performerBankName(name) {
    return имя(name).startsWith(IP_PREFIX) ? `${withoutForm(name)} (ИП)` : имя(name);
}
/** Для строки «Поставщик» счёта: «ИП Ханеев Дмитрий Олегович». */
export function performerShortName(name) {
    return имя(name).startsWith(IP_PREFIX) ? `ИП ${withoutForm(name)}` : имя(name);
}
/**
 * Реквизиты исполнителя для СЧЁТА — без банковских данных.
 *
 * Банк в счёте уже есть отдельной таблицей-платёжкой сверху, и повторять его
 * строкой незачем. Формат взят дословно, включая «Тел:» и точку перед ним.
 */
function supplierLine(performer) {
    return `${performerShortName(performer.name)}, ОГРНИП ${performer.ogrnip}, ИНН ${performer.inn}, ${performer.address}. Тел: ${performer.phone}`;
}
/**
 * Реквизиты исполнителя для АКТА — С банковскими данными.
 *
 * В акте платёжки нет, а расчёты он подтверждает, поэтому счёт и банк
 * называются прямо в реквизитах стороны.
 */
function performerDetailsLine(performer) {
    return (`ОГРНИП ${performer.ogrnip}, ИНН ${performer.inn}, ${performer.address}, ` +
        `р/с ${performer.rs} в банке ${performer.bank}, БИК ${performer.bik}, к/с ${performer.ks}.`);
}
/**
 * Название заказчика: «ООО «Академическая стоматология»».
 *
 * Форма СОКРАЩЁННАЯ в обоих документах. Полная («Общество с ограниченной
 * ответственностью») занимала полстроки и в акте, где реквизиты сторон и так
 * идут сплошным абзацем, только удлиняла его. Поле `legal.form` с полной
 * формой остаётся в справочнике — оно нужно договорам.
 */
function customerHead(legal) {
    return withForm(legal.shortForm, legal.name);
}
/** Общие для обоих документов сведения о заказчике. */
function customerIdentity(legal) {
    return `ОГРН ${legal.ogrn}, ИНН/КПП ${legal.inn}/${legal.kpp}, ${legal.address}`;
}
/**
 * Реквизиты заказчика для АКТА.
 *
 * Оборот «в лице …» появляется, только если подписант задан: ИП действует
 * лично, и в его актах этого оборота нет вовсе.
 */
function customerFullLine(legal) {
    const head = customerHead(legal);
    const inPerson = signerInPersonOf(legal);
    /*
     * «в {банк}», а не «в банке {банк}» — в отличие от исполнителя.
     *
     * Банк заказчика записан в предложном падеже и уже включает своё
     * существительное: «филиале «Центральный» банка ВТБ ПАО», «Бурятском
     * отделении №8601 ПАО Сбербанк», «АО «Тинькофф Банк»». Слово «банке»
     * перед ними даёт «в банке филиале» — так и было до сверки с актами.
     */
    const bank = `р/с ${legal.rs}, в ${legal.bank}, БИК ${legal.bik}, к/с ${legal.ks}`;
    return [
        inPerson ? `${head} в лице ${inPerson}` : head,
        customerIdentity(legal),
        bank,
    ].join(', ');
}
/** Сборка модели документа. Чистая функция: одинаковый вход — одинаковый документ. */
export function buildDocument(input) {
    const totalKop = documentTotalKop(input.items);
    const { legal, performer } = input;
    const contractDate = formatDateDots(legal.contractDate);
    return {
        kind: input.kind,
        number: input.number,
        numberSuffix: input.numberSuffix,
        facsimile: input.facsimile ?? null,
        title: input.kind === 'schet'
            ? `Счет № ${invoiceNumberText(input.number, input.numberSuffix)} от ${formatDateLong(input.date)} г.`
            : `Акт № ${invoiceNumberText(input.number, input.numberSuffix)} от ${formatDateLong(input.date)} г.`,
        day: dayOf(input.date),
        monthYear: monthYearGenitive(input.date),
        bank: {
            bankName: performer.bank,
            bik: performer.bik,
            ks: performer.ks,
            inn: performer.inn,
            // У ИП нет КПП — ячейка в документе присутствует, но остаётся пустой.
            kpp: '',
            rs: performer.rs,
            recipient: performerBankName(performer.name),
        },
        supplier: supplierLine(performer),
        // «ИП Ханеев Дмитрий Олегович,» — сокращённо, как и заказчик.
        performerName: `${performerShortName(performer.name)},`,
        performerDetails: performerDetailsLine(performer),
        customer: `${customerHead(legal)}, ${customerIdentity(legal)}`,
        customerFull: customerFullLine(legal),
        basis: legal.contractNo ? `Основной Договор № ${legal.contractNo}` : '',
        contractReference: legal.contractNo
            ? `по договору № ${legal.contractNo}${contractDate ? ` от ${contractDate} г.` : ''}`
            : 'по договору',
        items: deliveryLast(input.items).map((item, index) => ({
            index: index + 1,
            name: item.name,
            // Количество без хвоста «.00»: в документах лаборатории пишут «1».
            qty: Number.isInteger(item.qty) ? String(item.qty) : String(item.qty).replace(',', '.'),
            unit: item.unit,
            /*
              Формат суммы ОДИН на оба документа: «19500.00».
      
              Раньше счёт и акт печатали одно и то же число по-разному — счёт
              «14000.00», акт «14 000,00», — потому что каждый воспроизводил свой
              образец лаборатории. Но образцы набирали в разное время и в разных
              программах, а уходят счёт и акт вместе, одной сделкой. Две записи
              одной суммы в одном конверте — повод для вопроса из бухгалтерии,
              а не признак аккуратности.
            */
            price: formatKopDoc(item.priceKop),
            total: formatKopDoc(lineTotalKop(item.priceKop, item.qty)),
        })),
        itemCount: input.items.length,
        totalKop,
        total: formatKopDoc(totalKop),
        totalInWords: kopToWordsRu(totalKop),
        disclaimer: DISCLAIMER,
        performerSigner: performer.signer,
        customerSigner: signerSignatureOf(legal),
        customerShort: customerHead(legal),
        customerName: legal.name,
        performerShort: performerShortName(performer.name),
        /*
          «Генеральный директор, ООО "Василек"» в эталоне. У ИП должности нет,
          поэтому остаётся одно имя — выдумывать «директора» самому себе незачем.
        */
        performerRole: performerShortName(performer.name),
        totalInWordsAkt: kopToWordsRuDigits(totalKop),
    };
}
/**
 * Имя файла документа: «Счет №12 Медалист.docx».
 *
 * Номер и юрлицо, а не дата: у бухгалтера в папке лежат счета от многих
 * поставщиков, и находит он их по контрагенту. Дата же и так стоит внутри
 * документа, а в имени только удлиняла строку.
 *
 * Русские буквы Windows и macOS переносят нормально; вычищаются лишь символы,
 * которые файловая система не принимает, — иначе сохранение молча срывается.
 */
export function documentFileName(model, extension) {
    const label = model.kind === 'schet' ? 'Счет' : 'Акт';
    const customer = fileSafe(model.customerName);
    const номер = invoiceNumberText(model.number, model.numberSuffix);
    const name = customer ? `${label} №${номер} ${customer}` : `${label} №${номер}`;
    return `${name}.${extension}`;
}
/** Убрать из названия то, чего не бывает в именах файлов. */
function fileSafe(value) {
    return value
        .replace(/[\\/:*?"<>|]/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
//# sourceMappingURL=model.js.map